/**
 * AR-393 · Platform settings — the three settings on the Settings module's
 * Color Theme page: colour theme, display mode and text size.
 *
 * These are *platform* settings, not AIVIA preferences: they are attached to
 * the member's login rather than the device (Britta, 2026-09-21), which is why
 * the stored values live on the `users` row and this file is the one place both
 * the client and the NestJS backend read the allowed values from.
 *
 * The palettes themselves — the CSS variable ramps per theme and the
 * dark-appearance token set — belong to AR-390. This file only says which ids
 * exist, which one is the default, and how an unrecognised stored value is
 * coerced.
 *
 * NOTE: `client/public/loaders/platform-settings.js` repeats the three id lists
 * because it has to run before the bundle exists (it is what prevents a flash of
 * the wrong theme). If an id is added here, add it there too.
 */

export const COLOR_THEMES = [
  // default first (Jason, 2026-09-10)
  { id: 'clairvoyant', label: 'Clairvoyant', accent: '#6d2a7d', darkStep: '#4b0a5b' },
  { id: 'coral', label: 'Coral', accent: '#ff7540', darkStep: '#9e3e19' },
  { id: 'hibiscus', label: 'Hibiscus', accent: '#b82f74', darkStep: '#8a2257' },
  { id: 'teal', label: 'Teal', accent: '#008588', darkStep: '#00595c' },
  { id: 'govbay', label: 'Governor Bay', accent: '#2b38b6', darkStep: '#1d2782' },
] as const;

export type ColorTheme = (typeof COLOR_THEMES)[number]['id'];

export const COLOR_THEME_IDS = COLOR_THEMES.map((t) => t.id) as ColorTheme[];
export const DEFAULT_COLOR_THEME: ColorTheme = 'clairvoyant';

/**
 * Three values, not two: `system` is a stored choice in its own right and
 * resolves against the device at runtime. Storing the *resolved* appearance
 * would lose the choice, so the member's selection is what is persisted.
 */
export const DISPLAY_MODES = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'Use device setting' },
] as const;

export type DisplayMode = (typeof DISPLAY_MODES)[number]['id'];

export const DISPLAY_MODE_IDS = DISPLAY_MODES.map((m) => m.id) as DisplayMode[];
export const DEFAULT_DISPLAY_MODE: DisplayMode = 'light';

/** The appearance a display mode resolves to — always light or dark. */
export type ResolvedMode = 'light' | 'dark';

export const TEXT_SIZES = [
  { id: 'md', label: 'Default' },
  { id: 'lg', label: 'Larger' },
  { id: 'xl', label: 'Largest' },
] as const;

export type TextSize = (typeof TEXT_SIZES)[number]['id'];

export const TEXT_SIZE_IDS = TEXT_SIZES.map((t) => t.id) as TextSize[];
export const DEFAULT_TEXT_SIZE: TextSize = 'md';

export interface PlatformSettings {
  colorTheme: ColorTheme;
  displayMode: DisplayMode;
  textSize: TextSize;
}

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettings = {
  colorTheme: DEFAULT_COLOR_THEME,
  displayMode: DEFAULT_DISPLAY_MODE,
  textSize: DEFAULT_TEXT_SIZE,
};

export const isColorTheme = (value: unknown): value is ColorTheme =>
  typeof value === 'string' && (COLOR_THEME_IDS as string[]).includes(value);

export const isDisplayMode = (value: unknown): value is DisplayMode =>
  typeof value === 'string' && (DISPLAY_MODE_IDS as string[]).includes(value);

export const isTextSize = (value: unknown): value is TextSize =>
  typeof value === 'string' && (TEXT_SIZE_IDS as string[]).includes(value);

/**
 * Any stored value not in the list falls back to the default. Real members are
 * carrying the retired `honeygold` theme id, so this is a live case rather than
 * defensive coding.
 */
export const normalizeColorTheme = (value: unknown): ColorTheme =>
  isColorTheme(value) ? value : DEFAULT_COLOR_THEME;

export const normalizeDisplayMode = (value: unknown): DisplayMode =>
  isDisplayMode(value) ? value : DEFAULT_DISPLAY_MODE;

export const normalizeTextSize = (value: unknown): TextSize =>
  isTextSize(value) ? value : DEFAULT_TEXT_SIZE;

export const normalizePlatformSettings = (value: unknown): PlatformSettings => {
  const source = (value ?? {}) as Record<string, unknown>;
  return {
    colorTheme: normalizeColorTheme(source.colorTheme),
    displayMode: normalizeDisplayMode(source.displayMode),
    textSize: normalizeTextSize(source.textSize),
  };
};

/**
 * Where the browser keeps the last-applied settings so the pre-paint loader can
 * stamp them before the bundle has loaded. It is a cache of the member record,
 * never the source of truth — the server value overwrites it on every load.
 */
export const PLATFORM_SETTINGS_CACHE_KEY = 'aivia.platform-settings';

/**
 * The single boolean display mode used to live here. It is read once, migrated
 * onto the member record, and then removed; after that the member's saved
 * setting wins on every device.
 */
export const LEGACY_DARK_MODE_KEY = 'darkMode';
